import { createSelector } from "reselect";
import get from "lodash/get";
import filter from "lodash/filter";
import includes from "lodash/includes";
import sumBy from "lodash/sumBy";
import subtract from "lodash/subtract";
import max from "lodash/max";
import ceil from "lodash/ceil";
import reduce from "lodash/reduce";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import isArray from "lodash/isArray";
import {
  calculatePayMonthlyItems,
  calculatePayTodayItems,
  formatAddonsForPayMonthly,
  formatMessage,
  urlParam,
  lower
  // totalPlanAndAddonDiscount
} from "utils/helpers";
import { formatCurrency } from "utils/i18nFormatter";
import {
  currencyFormatSelector,
  referralCodeSelector,
  deliveryInfoSelector,
  personalDetailsSelector,
  selectedNumberSelector,
  selectedNumberTypeSelector,
  portInSelector,
  numberFormatSelector,
  localeSelector,
  phonesFormSelector,
  selfActivationSelector,
  genericSelector,
  selectedSANumberTypeSelector,
  multiSimOrderSelector,
  deliveryHomeSlotTypeSelector,
  inCompleteOrderSelector,
  orderDataSelector,
  connectionDetailsSelector,
  selectedNumberPriceDomain,
  selectedNumberCategoryDomain,
  formValuesSelector,
  promocodeOrderPreviewSelector
} from "ducks/selectors";
import {
  PREPAID_PLAN_SUB_TYPES,
  SIM_TYPES,
  PLAN_TYPE,
  SHIPMENT_METHOD,
  DEFAULT_PREPAID_VALUES,
  forms,
  CONTRACT_TYPES,
  THIRD_PARTY_URL
} from "utils/constants";
import { globalFormatSelector } from "./globalFormat";
import { getUniqueListBy } from "../../utils/helpers";
import { makeSelectExternalPendingOrderSelected } from "./order";
import head from "lodash/head";

export const selectPlanService = state =>
  get(state, "api.planService.data", {});

export const preCheckoutFormSelector = state =>
  get(state, "form.PreCheckoutForm.values", {});

export const preCheckoutPlanSelector = state =>
  get(state, "form.PreCheckoutForm.values.planType", null);

export const planServiceSelector = createSelector(
  selectPlanService,
  data => data
);

export const planAddonBenefitsSelector = state =>
  get(state, "api.planAndAddonBenefits.data", {});

export const pendingOrdersDomain = state =>
  get(state, "api.externalPendingOrders.data", []);

export const selectSelectedAddons = state =>
  get(state.api, "selectedAddons.data", {});

export const suggestedAddonDomain = state =>
  get(state.api, "suggestedAddons.data.productVariants", []);

export const preSelectedOrderAddonsSelector = state =>
  get(state.api, "validateIccId.data.selected_paid_addons", []);

export const preAddonSelector = state =>
  get(state.api, "preSelectedAddon.data.preSelectedAddon", false);

export const planProductCategorySelector = state =>
  get(state, "api.planService.data.productCategory", {});

export const initialSelectedAddonDomain = state =>
  get(state, "api.initialSelectedAddon.data", "");

// ***************************************************************
// ******************** planService selectors ********************
// ***************************************************************

export const planTypeSelector = createSelector(
  planServiceSelector,
  planService => get(planService, "productCategory", "")
);

export const initialSelectedAddonSelector = createSelector(
  initialSelectedAddonDomain,
  data => data
);

export const basicPriceSelector = createSelector(
  planServiceSelector,
  planService => {
    return (
      Number(
        get(planService, "attributes", []).find(
          attribute => attribute.name === "price"
        )?.value
      ) ||
      get(planService, "basicPrice") ||
      0
    );
  }
);

export const basicTaxSelector = createSelector(
  planServiceSelector,
  genericSelector,
  (planService, generic) =>
    get(planService, "tax_percentage") || get(generic, "tax_percentage") || 0
);

export const multiSimSelector = urlParam("ms");

export const basicDataSelector = createSelector(
  planServiceSelector,
  planService => get(planService, "data") || 0
);

export const enabledAddonTypesSelector = createSelector(
  genericSelector,
  generic => get(generic, "enabledAddonTypes", [])
);

export const planAddonsSelector = createSelector(
  planServiceSelector,
  enabledAddonTypesSelector,
  (planService, enabledAddonTypes) => {
    // if (isArray(get(planService, "allowed_products"))) {
    //   return filter(get(planService, "additional_products"), product =>
    //     includes(get(planService, "allowed_products"), product.id)
    //   );
    // } else if (!isEmpty(enabledAddonTypes)) {
    //   // Only the Additional Product types that are in the enabledAddonTypes should be included
    //   return filter(get(planService, "additional_products"), product =>
    //     includes(enabledAddonTypes, product.type)
    //   );
    // }
    return get(planService, "associatedProductVariants", []);
  }
);

export const defaultPlanAddonsSelector = createSelector(
  planServiceSelector,
  planService =>
    filter(get(planService, "default_products", []), addon =>
      get(addon, "visible")
    )
);

export const defaultInvisiblePlanAddonsSelector = createSelector(
  planServiceSelector,
  planService =>
    filter(
      get(planService, "default_products", []),
      addon => !get(addon, "visible")
    )
);

export const payMonthlySelector = createSelector(
  planServiceSelector,
  planService => get(planService, "pay_monthly", [])
);

export const payTodaySelector = createSelector(
  planServiceSelector,
  planService => get(planService, "pay_today", [])
);

export const planSubTypeSelector = createSelector(
  planServiceSelector,
  planService => get(planService, "sub_type", "")
);

export const suggestedAddonSelector = createSelector(
  currencyFormatSelector,
  suggestedAddonDomain,
  selectSelectedAddons,
  basicTaxSelector,
  genericSelector,
  (currencyFormat, suggestedAddons, selectedAddons, basicTax, generic) => {
    const addons = get(generic, "show_only_actionable_addons")
      ? filter(suggestedAddons, addon => addon.action)
      : suggestedAddons;
    const thirdPartyAddons = addons.filter(addon => addon.serviceType === "third_party");
  
    return formatAddonsForPayMonthly(
      thirdPartyAddons,
      selectedAddons,
      currencyFormat,
      basicTax
    );
  }
);

export const addonsSelector = createSelector(
  currencyFormatSelector,
  planAddonsSelector,
  selectSelectedAddons,
  basicTaxSelector,
  genericSelector,
  (currencyFormat, planAddons, selectedAddons, basicTax, generic) => {
    const addons = get(generic, "show_only_actionable_addons")
      ? filter(planAddons, addon => addon.action)
      : planAddons;
    return formatAddonsForPayMonthly(
      addons,
      selectedAddons,
      currencyFormat,
      basicTax
    );
  }
);

export const addonsSelectorLabels = createSelector(
  selectSelectedAddons,
  state => get(state, "api.eligiblePlans.data.productVariants", []),
  (selectedAddons, eligiblePlans) => {
    return (
      Array.from(
        new Set(
          Object.keys(selectedAddons)
            .filter(addon => selectedAddons[addon])
            ?.reduce((previous, addon) => {
              let selectedItemLabels =
                eligiblePlans.find(plan => plan.sku === addon)?.labels || [];
              return [...previous, ...selectedItemLabels];
            }, [])
        )
      ) || []
    );
  }
);

export const selectedAddonCount = createSelector(
  planAddonsSelector,
  selectSelectedAddons,
  (planAddons, selectedAddons) => {
    return (
      planAddons &&
      planAddons.reduce(
        (count, addon) =>
          get(selectedAddons, addon.id || addon.sku) ? count + 1 : count,
        0
      )
    );
  }
);

export const defaultAddonsSelector = createSelector(
  currencyFormatSelector,
  defaultPlanAddonsSelector,
  basicTaxSelector,
  (currencyFormat, defaultAddons, basicTax) => {
    const selectedAddons = {};
    // all default addons are selected by default
    for (const addon of defaultAddons) {
      selectedAddons[addon.id] = true;
    }
    return formatAddonsForPayMonthly(
      defaultAddons,
      selectedAddons,
      currencyFormat,
      basicTax
    );
  }
);

export const defaultInvisibleAddonsSelector = createSelector(
  currencyFormatSelector,
  defaultInvisiblePlanAddonsSelector,
  basicTaxSelector,
  (currencyFormat, defaultInvisibleAddons, basicTax) => {
    const selectedAddons = {};
    // all default addons are selected by default
    for (const addon of defaultInvisibleAddons) {
      selectedAddons[addon.id] = true;
    }
    return formatAddonsForPayMonthly(
      defaultInvisibleAddons,
      selectedAddons,
      currencyFormat,
      basicTax
    );
  }
);

// ***************************************************************
// ******************* planService makeSelect ********************
// ***************************************************************

export const makeSelectSuggestedAddons = () => suggestedAddonSelector;

export const makeSelectPayMonthlyItems = () =>
  createSelector(
    selectedNumberPriceDomain,
    selectedNumberSelector,
    basicPriceSelector,
    basicTaxSelector,
    payMonthlySelector,
    currencyFormatSelector,
    referralCodeSelector,
    defaultPlanAddonsSelector,
    planSubTypeSelector,
    basicDataSelector,
    phonesFormSelector,
    genericSelector,
    selectedSANumberTypeSelector,
    planServiceSelector,
    additionalAddonsSelector,
    suggestedAddonSelector,
    selectedNumberTypeSelector,
    selfActivationSelector,
    genericSelector,
    personalDetailsSelector,
    formValuesSelector(forms.CONNECTION_DETAILS_FORM),
    pendingOrdersDomain,
    makeSelectExternalPendingOrderSelected(),
    (
      selectedNumberPrice,
      goldenNumber,
      basicPrice,
      basicTax,
      monthlyItems,
      currencyFormat,
      referralCode,
      defaultPlanAddons,
      planSubType,
      basicData,
      phonesForm,
      genericData,
      numberType,
      planDetails,
      addons,
      suggestedAddons,
      selectedNumberType,
      selfActivation,
      generic,
      personalDetails,
      formValues,
      pendingOrders,
      pendingOrderSelected
    ) => {
      const precision = get(currencyFormat, "precision", 2);
      const deliveryMethod = get(personalDetails, "shippingMethod.name");
      let deliveryFee = 0;

      // Delivery Fee should be calculated irrespective of Prepaid/Postpaid
      if (deliveryMethod === SHIPMENT_METHOD.DOORSTEP_DELIVERY) {
        deliveryFee = get(personalDetails, "delivery_slot_fee", 0);
      }
      if (deliveryMethod === SHIPMENT_METHOD.SELF_PICKUP) {
        deliveryFee = get(personalDetails, "selfPickUpLocation.deliveryFee", 0);
      }

      const payMonthlyItems = includes(PREPAID_PLAN_SUB_TYPES, planSubType)
        ? []
        : calculatePayMonthlyItems({
            basicPrice,
            basicTax,
            items: monthlyItems,
            currencyFormat,
            referralCode,
            addons: getUniqueListBy([...addons, ...suggestedAddons], "id"),
            defaultPlanAddons,
            phonesForm,
            discount: get(genericData, "transfer_number_discount", {}),
            planDetails,
            numberType,
            selectedNumberType,
            selfActivation,
            generic
          });
      const originalPrice = max([
        sumBy(payMonthlyItems, ({ value }) => max([value, 0])),
        0
      ]);
      const originalFixedPrice = formatCurrency(originalPrice, {
        style: "short"
      });
      const originalPriceWithoutTax = ceil(
        max([
          sumBy(payMonthlyItems, ({ value_without_tax }) =>
            max([value_without_tax, 0])
          ),
          0
        ]),
        precision
      );
      const originalFixedPriceWithoutTax = formatCurrency(
        originalPriceWithoutTax,
        { style: "short" }
      );

      const goldenNumberPrice = selectedNumberPrice
        ? selectedNumberPrice?.value
        : goldenNumber?.price?.value;
      const goldenPrice =
        get(formValues, "number_type") !== CONTRACT_TYPES.PORT_IN
          ? goldenNumberPrice ?? 0
          : 0;

      const totalAmount =
        max([sumBy(payMonthlyItems, "value"), 0]) +
        deliveryFee +
        goldenPrice +
        (!isEmpty(pendingOrderSelected) && !pendingOrderSelected?.charged
          ? pendingOrderSelected?.price
          : 0);

      const totalAmountWithoutTax =
        ceil(max([sumBy(payMonthlyItems, "value_without_tax"), 0]), precision) +
        deliveryFee;
      const totalTaxAmount = ceil(
        totalAmount - totalAmountWithoutTax,
        precision
      );
      const totalData = max([sumBy(payMonthlyItems, "data"), 0]) + basicData;
      const bundleRealPrice = payMonthlyItems.find(
        item => item?.productCode === "product_bundle" && item?.selected
      );
      const isDiscount =
        bundleRealPrice?.originalPrice > bundleRealPrice?.discountedPrice;

      let discountValue = 0;
      for (let i = 0; i < referralCode?.benefits?.length; i++) {
        discountValue += parseFloat(
          referralCode?.benefits[i]?.fulfillment_meta?.discountValue
        );
      }
      //Added the below to fix the display of total price in all screens
      const bundleTotalPrice =
        totalAmount +
        (bundleRealPrice?.originalPrice - bundleRealPrice?.discountedPrice);

      if (typeof discountValue !== "number") discountValue = 0;
      const totalPrice = totalAmount ?? 0;
      const finalPriceAfterDiscount =
        totalPrice >= discountValue ? totalPrice - discountValue : 0;
      return {
        items: payMonthlyItems,
        originalPrice: formatCurrency(originalPrice, { style: "short" }),
        originalFixedPrice: formatCurrency(originalFixedPrice, {
          style: "short"
        }),
        originalPriceWithoutTax: formatCurrency(originalPriceWithoutTax, {
          style: "short"
        }),
        originalFixedPriceWithoutTax,
        totalPrice: formatCurrency(totalAmount, {
          style: "short"
        }),
        totalPriceWithoutTax: formatCurrency(totalAmountWithoutTax, {
          style: "short"
        }),
        totalTaxPrice: formatCurrency(totalTaxAmount, { style: "long" }),
        totalFixedPrice: formatCurrency(totalAmount, { style: "long" }),
        bundleRealPrice: formatCurrency(bundleTotalPrice, {
          style: "long"
        }),
        totalFixedPriceWithoutTax: formatCurrency(totalAmountWithoutTax, {
          style: "long"
        }),
        totalData,
        totalAmount,
        totalAmountWithoutTax,
        totalTaxAmount,
        discountValue,
        finalPriceAfterDiscount,
        referralCode,
        isDiscount
      };
    }
  );

export const makeSelectPayTodayItems = page =>
  createSelector(
    payTodaySelector,
    payMonthlySelector,
    currencyFormatSelector,
    referralCodeSelector,
    deliveryInfoSelector,
    personalDetailsSelector,
    selectedNumberSelector,
    selectedNumberTypeSelector,
    portInSelector,
    basicPriceSelector,
    basicTaxSelector,
    addonsSelector,
    defaultPlanAddonsSelector,
    planSubTypeSelector,
    numberFormatSelector,
    localeSelector,
    phonesFormSelector,
    genericSelector,
    selectedSANumberTypeSelector,
    selfActivationSelector,
    preSelectedOrderAddonsSelector,
    deliveryHomeSlotTypeSelector,
    orderDataSelector,
    connectionDetailsSelector,
    (
      todayItems,
      monthlyItems,
      currencyFormat,
      referralCode,
      deliveryInfo,
      personalDetails,
      selectedNumber,
      selectedNumberType,
      portInForm,
      basicPrice,
      basicTax,
      addons,
      defaultPlanAddons,
      planSubType,
      numberFormat,
      locale,
      phonesForm,
      genericData,
      numberType,
      selfActivationForm,
      preActivationDeliveryAddon,
      deliveryHomeSlotType,
      order,
      connectionDetails
    ) => {
      const payMonthlyItems = !includes(PREPAID_PLAN_SUB_TYPES, planSubType)
        ? []
        : calculatePayMonthlyItems({
            basicPrice,
            basicTax,
            items: monthlyItems,
            currencyFormat,
            referralCode,
            addons,
            defaultPlanAddons,
            phonesForm,
            discount: get(genericData, "transfer_number_discount", {}),
            numberType
          });

      const payTodayItems = calculatePayTodayItems({
        items: todayItems,
        currencyFormat,
        referralCode,
        deliveryInfo,
        personalDetails,
        selectedNumber,
        selectedNumberType,
        portInForm,
        page,
        numberFormat,
        locale,
        phonesForm,
        basicTax,
        selfActivationForm,
        preActivationDeliveryAddon,
        deliveryHomeSlotType,
        order,
        connectionDetails
      });

      const allPayTodayItems = [...payMonthlyItems, ...payTodayItems];

      const totalAmount = max([sumBy(allPayTodayItems, "value"), 0]);
      return {
        items: allPayTodayItems,
        totalPrice: formatCurrency(totalAmount, { style: "short" }),
        totalFixedPrice: formatCurrency(totalAmount, { style: "long" }),
        totalAmount
      };
    }
  );

export const makeSelectOrderSummaryItems = page =>
  createSelector(
    selectedNumberCategoryDomain,
    selectedNumberPriceDomain,
    selectedNumberSelector,
    planServiceSelector,
    currencyFormatSelector,
    addonsSelector,
    suggestedAddonSelector,
    preCheckoutFormSelector,
    makeSelectPayTodayItems(),
    referralCodeSelector,
    planAndAddonBenefitsSelector,
    globalFormatSelector,
    formValuesSelector(forms.CONNECTION_DETAILS_FORM),
    promocodeOrderPreviewSelector,
    selectSelectedAddons,
    basicTaxSelector,
    pendingOrdersDomain,
    makeSelectExternalPendingOrderSelected(),
    (
      numberCategorySelected,
      seletedNumberPrice,
      goldenNumber,
      planService,
      currencyFormat,
      addons,
      suggestedAddons,
      checkoutFormValues,
      payTodayItems,
      referralCode,
      planAndAddonBenefits,
      globalFormat,
      formValues,
      orderPreview,
      userSelectedAddons,
      basicTax,
      pendingOrders,
      pendingOrderSelected
    ) => {
      const planPrice = get(planService, "price") || 0;
      const planDiscountPrice = get(planService, "discountedPrice");
      const totalPlanDiscount =
        planDiscountPrice >= planPrice
          ? 0
          : subtract(planPrice - planDiscountPrice) || 0;
      const selectedAddon = filter(
        getUniqueListBy([...addons, ...suggestedAddons], "id"),
        addon => {
          return addon.selected;
        }
      );

      const finalAddonDiscount = reduce(
        selectedAddon,
        (acc, addon) => {
          const addonPrice = get(addon, "oldPrice") || 0;
          const addonDiscount = get(addon, "value");
          const totalAddonDiscount =
            addonDiscount >= addonPrice
              ? addonPrice
              : subtract(addonPrice - addonDiscount) || 0;
          return acc + totalAddonDiscount;
        },
        0
      );

      const totalPlanAndAddonDiscount = totalPlanDiscount + finalAddonDiscount;

      const recurrence =
        get(planService, "attributes", []).find(
          attribute => attribute.name === "recurrent"
        )?.value !== "false"
          ? "/mo"
          : "1";

      const planDetails = [
        {
          label: planService.title,
          features: planService.features,
          price:
            formatCurrency(planPrice, {
              style: "short"
            }) + recurrence,
          value: planPrice
        }
      ];
      let selectedAddons = getUniqueListBy(
        [...addons, ...suggestedAddons],
        "id"
      )
        .filter(addon => addon.selected)
        .map(addon => {
          return {
            ...addon,
            price: `${formatCurrency(addon.originalPrice, {
              style: "short"
            })}/${addon.recurrence}`
          };
        });

      const selectedGoldenNumber = goldenNumber
        ? [
            {
              label:
                goldenNumber?.numberCategory ||
                goldenNumber?.category ||
                goldenNumber?.subCategory + " number",
              features: [goldenNumber?.number],
              originalPrice: seletedNumberPrice
                ? seletedNumberPrice?.value
                : goldenNumber?.price?.value
            }
          ]
        : [];

      const goldenNumberPrice =
        get(formValues, "number_type") !== CONTRACT_TYPES.PORT_IN
          ? seletedNumberPrice?.value ?? 0
          : 0;

      const mainsubtotal =
        selectedAddons.reduce(
          (stotal, addon) => addon.originalPrice + stotal,
          0
        ) + planPrice;

      const oneTimeSubtotal =
        get(payTodayItems, "totalAmount", 0) + goldenNumberPrice;

      let totalAmount =
        mainsubtotal +
        oneTimeSubtotal +
        goldenNumberPrice -
        totalPlanAndAddonDiscount;
      let discountValue = 0;
      for (let i = 0; i < referralCode?.benefits?.length; i++) {
        discountValue += parseFloat(
          referralCode?.benefits[i]?.fulfillment_meta?.discountValue
        );
      }
      if (typeof discountValue !== "number") discountValue = 0;
      const totalPrice = totalAmount ?? 0;
      totalAmount =
        totalPrice >= discountValue
          ? formatCurrency(totalPrice - discountValue, {
              style: "long"
            })
          : 0;

      const deliverySection = get(payTodayItems, "items", []).filter(
        item => item.type === "delivery"
      );

      const planType = get(checkoutFormValues, "planType");
      let orderSummary = {};

      if (
        head(pendingOrders?.carts)?.channel?.toLowerCase() ===
          THIRD_PARTY_URL &&
        pendingOrderSelected
      ) {
        totalAmount = pendingOrderSelected.charged
          ? totalAmount
          : parseFloat(totalAmount) + pendingOrderSelected.price;
        const tmpExternalOrders = [
          {
            cartId: pendingOrderSelected.id,
            channel: pendingOrderSelected.channel,
            charged: pendingOrderSelected.charged,
            features: pendingOrderSelected.features,
            label: pendingOrderSelected.title,
            price: pendingOrderSelected.price,
            value: pendingOrderSelected.price
          }
        ];
        orderSummary = {
          totalAmount: formatCurrency(totalAmount, { style: "short" }),
          totalPrice: totalAmount,
          mainProducts:
            get(formValues, "number_type") === CONTRACT_TYPES.PORT_IN
              ? { sections: [...tmpExternalOrders, ...deliverySection] }
              : {
                  sections: [
                    ...tmpExternalOrders,
                    ...deliverySection,
                    ...selectedGoldenNumber
                  ]
                },
          oneTimeProducts: {},
          discountItems: [
            ...(finalAddonDiscount > 0
              ? [
                  {
                    title: "total_addon_discount",
                    value: `-${formatCurrency(finalAddonDiscount, {
                      style: "short"
                    })}`
                  }
                ]
              : [])
          ]
        };
      } else {
        if (lower(planType) === PLAN_TYPE["PREPAID"]) {
          if (
            planService?.productCode === "product_bundle" &&
            planService?.sku !== DEFAULT_PREPAID_VALUES.sku
          ) {
            selectedAddons = [...planDetails, ...selectedAddons];
          }
          if (get(formValues, "number_type") === CONTRACT_TYPES.PORT_IN) {
            orderSummary = {
              totalAmount: formatCurrency(totalAmount, { style: "short" }),
              totalPrice: totalAmount,
              mainProducts: {
                sections: [...selectedAddons, ...deliverySection]
              },
              oneTimeProducts: {},
              discountItems: [
                ...(finalAddonDiscount > 0
                  ? [
                      {
                        title: "total_addon_discount",
                        value: `-${formatCurrency(finalAddonDiscount, {
                          style: "short"
                        })}`
                      }
                    ]
                  : [])
              ]
            };
          } else {
            orderSummary = {
              totalAmount: formatCurrency(totalAmount, { style: "short" }),
              totalPrice: totalAmount,
              mainProducts: {
                sections: [
                  ...selectedAddons,
                  ...deliverySection,
                  ...selectedGoldenNumber
                ]
              },
              oneTimeProducts: {},
              discountItems: [
                ...(finalAddonDiscount > 0
                  ? [
                      {
                        title: "total_addon_discount",
                        value: `-${formatCurrency(finalAddonDiscount, {
                          style: "short"
                        })}`
                      }
                    ]
                  : [])
              ]
            };
          }
        } else {
          orderSummary = {
            totalAmount: formatCurrency(totalAmount, { style: "short" }),
            totalPrice: totalAmount,
            mainProducts: {
              sections: [...planDetails, ...selectedAddons]
            },
            oneTimeProducts: {
              subtotal: formatCurrency(oneTimeSubtotal, { style: "long" }),
              sections: [...deliverySection, ...selectedGoldenNumber]
            },
            discountItems: [
              ...(totalPlanDiscount > 0
                ? [
                    {
                      title: "total_plan_discount",
                      value: `-${formatCurrency(totalPlanDiscount, {
                        style: "short"
                      })}`
                    }
                  ]
                : []),
              ...(finalAddonDiscount > 0
                ? [
                    {
                      title: "total_addon_discount",
                      value: `-${formatCurrency(finalAddonDiscount, {
                        style: "short"
                      })}`
                    }
                  ]
                : [])
            ]
          };
          if (!isEmpty(deliverySection)) {
            orderSummary.mainProducts.subtotal = formatCurrency(mainsubtotal, {
              style: "short"
            });
          }
        }
      }

      let userSelectedSku = userSelectedAddons;
      if (
        head(pendingOrders?.carts)?.channel?.toLowerCase() ===
          THIRD_PARTY_URL &&
        pendingOrderSelected
      ) {
        userSelectedSku = {
          [pendingOrderSelected.sku]: true
        };
      }
      const userAddons = filter(
        Object.keys(userSelectedSku),
        key => userSelectedSku[key]
      );

      const additionalProductsItems = filter(
        get(orderPreview, "items"),
        item =>
          !userAddons.includes(item.sku) &&
          !isEmpty(item?.itemDiscounts) &&
          !(item.itemType === "EXTERNAL_REWARD" || item.itemType === "FEE")
      );

      const additionalProducts = filter(
        additionalProductsItems,
        additionalProduct => !isEmpty(additionalProduct?.product)
      )?.map(item => ({
        ...item.product,
        price: item.originalCost.amount
      }));

      const focItems = additionalProducts.length
        ? formatAddonsForPayMonthly(
            additionalProducts,
            {},
            currencyFormat,
            basicTax
          )
        : [];

      if (!(focItems?.length === 0)) {
        orderSummary = {
          ...orderSummary,
          focProducts: {
            sections: [...focItems]
          }
        };
      }
      return orderSummary;
    }
  );

export const additionalAddonsSelector = createSelector(
  addonsSelector,
  selfActivationSelector,
  currencyFormatSelector,
  genericSelector,
  multiSimOrderSelector,
  ([...addOns], selfActivation, currencyFormat, generic, multiSim) => {
    const multi_sim = get(selfActivation, "multi_sim", false);
    const sim_type = get(selfActivation, "sim_type");
    if ((multiSim || multi_sim) && sim_type !== SIM_TYPES.ESIM) {
      const multi_sim_price = get(generic, "multi_sim.price");
      addOns.unshift({
        label: `${get(generic, "multi_sim.label")}`,
        value: multi_sim_price,
        selected: true,
        type: "sim",
        price: formatCurrency(multi_sim_price, { style: "short" })
      });
    }
    return addOns;
  }
);

export const planAndAddonBenefitsSelector = createSelector(
  planAddonBenefitsSelector,
  benefitsItem => {
    let finalBenefits = isArray(benefitsItem)
      ? benefitsItem.reduce((acc, list) => {
          const { benefits } = list;
          map(benefits, benefititem => {
            const listItem = {
              ...benefititem,
              label: `${benefititem.selectedType}_label`,
              numberof_months: `${benefititem.numberOfMonths} months`
            };
            acc.push(listItem);
          });
          return acc;
        }, [])
      : [];
    return finalBenefits;
  }
);

export const makeSelectAddons = () => addonsSelector;

export const makeSelectAddonsLabels = () => addonsSelectorLabels;

export const makeSelectDefaultPlanAddons = () => defaultAddonsSelector;

export const makeSelectSelectedAddons = () => additionalAddonsSelector;

export const makeSelectconfirmedAddon = () =>
  createSelector(additionalAddonsSelector, ([...addOns]) =>
    addOns
      .filter(addon => addon.selected && get(addon, "type") !== "sim")
      .map(addon => addon.label)
      .join()
  );

export const makeSelectSimAddons = () =>
  createSelector(additionalAddonsSelector, ([...addOns]) =>
    addOns
      .filter(addon => addon.selected && get(addon, "type") === "sim")
      .map(addon => addon.label)
      .join()
  );

export const makeSelectSimCount = () =>
  createSelector(selfActivationSelector, self_activation =>
    get(self_activation, "sim_count")
  );

export const makeSelectPreSelectedAddons = () =>
  createSelector(
    preSelectedOrderAddonsSelector,
    inCompleteOrderSelector,
    (activationAddons, incompleteaddons) =>
      get(incompleteaddons, "selected_addons", []) || activationAddons
  );

export const planDetailsSelector = createSelector(
  selfActivationSelector,
  localeSelector,
  planServiceSelector,
  (selfActivation, locale, { ...planData }) => {
    let payMonthlyItems = get(planData, "pay_monthly", false);
    const sim_count = get(selfActivation, "sim_count");
    if (sim_count && payMonthlyItems) {
      const data = formatMessage(get(locale, "family_plan_sims"), {
        sim_count
      });
      planData["pay_monthly"] = [
        ...payMonthlyItems,
        { label: data, type: "sim", value: 0 }
      ];
    }
    return planData;
  }
);

export const makeSelectPlanDetails = () => planDetailsSelector;

export const makeSelectPlanFAQ = () =>
  createSelector(planServiceSelector, planService => get(planService, "faq"));

export const makeSelectPlanName = () =>
  createSelector(planServiceSelector, planService => get(planService, "name"));

export const makeSelectPlanTextName = () =>
  createSelector(
    planServiceSelector,
    selectSelectedAddons,
    (planService, addon) => {
      if (get(planService, "sku") === "PREPAID_SKU") {
        return !isEmpty(addon) ? "prepaid" : "";
      }
      return get(planService, "title");
    }
  );

export const makeSelectSpecialNumberAllowed = () =>
  createSelector(planServiceSelector, planService =>
    get(planService, "special_number_allowed")
  );

export const makeSelectFamilyPlanSims = () =>
  createSelector(planDetailsSelector, ({ ...plan }) => {
    let payMonthly = get(plan, "pay_monthly", []);
    let simItems = filter(payMonthly, plan => get(plan, "type") === "sim")
      .reduce((acc, item) => {
        acc.push(item.label);
        return acc;
      }, [])
      .join(",");
    return simItems;
  });

export const makeSelectPayDevice = () =>
  createSelector(
    phonesFormSelector,
    ({ phoneVariant, phonePaymentOption, paymentTerm }) => {
      if (phoneVariant) {
        return {
          label: phoneVariant.nameWithSpecs || phoneVariant.title,
          paymentType: phonePaymentOption,
          paymentTerm: paymentTerm,
          paymentOption: phoneVariant.paymentOption,
          deviceSku: phoneVariant.id
        };
      }
      return null;
    }
  );

export const makeSelectAddonPreselected = () => preAddonSelector;

export const makeSelectPlanType = () => planProductCategorySelector;

export const makeSelectInitialSelectedAddon = () =>
  initialSelectedAddonSelector;

export const makePreCheckoutPlanSelector = () => preCheckoutPlanSelector;
